import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';

@Injectable()
export class TerminologyService {
  constructor(private _api: ApiService) {}

  getTerminologyDetailsOfSpecificProject(id: string): Observable<any> {
    const results = this._api.get(`/project/${id}`).pipe(map((data) => data));
    return results;
  }

  getConceptTreePositions(
    projectId: string,
    conceptId: string,
    limit?: number,
    offset?: number,
    sort?: string,
    ascending?: boolean,
  ): Observable<any> {
    const results = this._api
      .get(`/project/${projectId}/concept/${conceptId}/trees?limit=${limit}&offset=${offset}&sort=${sort}&ascending=${ascending}&handler=wildcard`)
      .pipe(map((data) => data));

    return results;
  }

  getConceptTreeChildrenPositions(
    projectId: string,
    code: string,
    limit?: number,
    offset?: number,
    ascending?: boolean,
    sort?: string,
  ): Observable<any> {
    const results = this._api
      .get(
        `/project/${projectId}/concept/${code}/trees/children?limit=${limit}&offset=${offset}&ascending=${ascending}&sort=${sort}&handler=wildcard`,
      )
      .pipe(map((data) => data));

    return results;
  }

  getTopLevelConceptTreeChildrenPositions(
    projectId: string,
    concept: string,
    code: string,
    limit?: number,
    offset?: number,
    ascending?: boolean,
    sort?: string,
  ): Observable<any> {
    const results = this._api
      .get(
        `/project/${projectId}/concept/${concept}/${code}/trees/children?limit=${limit}&offset=${offset}&ascending=${ascending}&sort=${sort}&handler=wildcard`,
      )
      .pipe(map((data) => data));

    return results;
  }

  getConceptRelationships(
    projectId: string,
    conceptId: string,
    relationshipType: string,
    limit?: number,
    offset?: number,
    filter?: string,
    sort = 'type',
    ascending = false,
  ): Observable<any> {
    const results = this._api
      .get(
        `/project/${projectId}/concept/${conceptId}/${relationshipType}?limit=${limit}&offset=${offset}&sort=${sort}&ascending=${ascending}&query=${encodeURIComponent(
          filter,
        )}&handler=wildcard`,
      )
      .pipe(map((data) => data));

    return results;
  }

  getConceptById(projectId: string, conceptId: string): Observable<any> {
    const results = this._api.get(`/project/${projectId}/concept/${conceptId}?include=summary,children,parents`).pipe(map((data) => data));
    return results;
  }
  getBrowserSearchResults(
    projectId: string,
    query: string,
    terminology: string,
    limit?: string,
    offset?: string,
    active?: boolean,
    leaf?: boolean,
    sort = 'name',
    ascending = true,
  ): Observable<any> {
    const encodedQuery = encodeURIComponent(query);
    const activeClause = active ? '&active=true' : '';
    const leafClause = leaf ? '&leaf=true' : '';
    const sortClause = sort == null ? '' : `&sort=${sort}&ascending=${ascending}`;
    const results = this._api
      .get(
        `/project/${projectId}/concept?terminology=${terminology}&query=${encodedQuery}&offset=${offset}&limit=${limit}${activeClause}${leafClause}&handler=browser&include=semanticTypes${sortClause}`,
      )
      .pipe(map((data) => data));

    return results;
  }

  getBrowserSearchResultsExport(
    filename: string,
    projectId: string,
    terminology: string,
    query: string,
    expression: string,
    active?: boolean,
    leaf?: boolean,
  ): Observable<void> {
    const encodedQuery = encodeURIComponent(query);
    const activeClause = active ? '&active=true' : '';
    const leafClause = leaf ? '&leaf=true' : '';
    return this._api.getExport(
      `/project/${projectId}/concept/export?terminology=${terminology}&query=${encodedQuery}&expression=${expression}${activeClause}${leafClause}&handler=browser`,
      filename,
    );
  }

  getBrowserSearchResultsWithExpression(
    projectId: string,
    query: string,
    terminology: string,
    expression?: string,
    limit?: string,
    offset?: string,
    active?: boolean,
    leaf?: boolean,
    sort = 'name',
    ascending = true,
  ): Observable<any> {
    const encodedQuery = encodeURIComponent(query);
    const encodedExpression = encodeURIComponent(expression);
    const activeClause = active ? '&active=true' : '';
    const leafClause = leaf ? '&leaf=true' : '';
    const sortClause = sort == null ? '' : `&sort=${sort}&ascending=${ascending}`;
    const results = this._api
      .get(
        `/project/${projectId}/concept?query=${encodedQuery}&terminology=${terminology}&expression=${encodedExpression}&limit=${limit}&offset=${offset}${activeClause}${leafClause}&handler=browser&include=semanticTypes${sortClause}`,
      )
      .pipe(map((data) => data));

    return results;
  }

  getTerminologies(query: string, page: number, perPage: number, sort = 'created', ascending = false): Observable<any> {
    return this._api
      .get(`/terminology?query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}&sort=${sort}&ascending=${ascending}`)
      .pipe(map((data: any) => data));
  }

  getTerminologyOrMapsetNativeExport(projectId: string, terminologyId: string, filename: string, action: string, format: string): Observable<void> {
    return this._api.getExport(`/project/${projectId}/${action}/${terminologyId}/export?format=${format}`, filename);
  }

  findMetadata(terminologyId: string): Observable<any> {
    const results = this._api.get(`/terminology/${terminologyId}/metadata`).pipe(map((data) => data));

    return results;
  }
  getProjectMetadata(projectId: string): Observable<any> {
    const results = this._api.get(`/project/${projectId}/metadata`).pipe(map((data) => data));

    return results;
  }

  getProjectTerminologyMetadata(projectId: string, limit?: number, offset?: number, query?: string): Observable<any> {
    const results = this._api
      .get(`/project/${projectId}/metadata?limit=${limit}&query=${encodeURIComponent(query)}&offset=${offset}`)
      .pipe(map((data) => data));

    return results;
  }

  getProjectTerminologyBulkSearch(
    projectId: string,
    data: string,
    terminology: string,
    expression: string,
    limit: number,
    active: boolean,
    leaf: boolean,
  ): Observable<any> {
    const activeClause = active ? '&active=true' : '';
    const encodedExpression = encodeURIComponent(expression);
    const leafClause = leaf ? '&leaf=true' : '';
    const results = this._api
      .post(
        `/project/${projectId}/concept/bulk?terminology=${terminology}&expression=${encodedExpression}&limit=${limit}${activeClause}${leafClause}&handler=browser`,
        data,
      )
      .pipe(map((data) => data));

    return results;
  }

  getProjectTerminologyBulkExport(
    projectId: string,
    data: string,
    terminology: string,
    expression: string,
    limit: number,
    active: boolean,
    leaf: boolean,
    filename,
  ): Observable<any> {
    const encodedExpression = encodeURIComponent(expression);
    const activeClause = active ? '&active=true' : '';
    const leafClause = leaf ? '&leaf=true' : '';
    return this._api.postExport(
      `/project/${projectId}/concept/bulk/export?terminology=${terminology}&expression=${encodedExpression}&limit=${limit}${activeClause}${leafClause}&handler=browser`,
      data,
      filename,
    );
  }

  uploadImportFile(terminology: string, version: number, file?: FormData): Observable<any> {
    const results = this._api.postFile(`/updater/terminology/upload?terminology=${terminology}&version=${version}`, file);
    return results;
  }

  getTermSuggestions(projectId, terminologies, term) {
    return this._api
      .get(`/project/${projectId}/autocomplete?terminology=${terminologies}&query=${encodeURIComponent(term)}`)
      .pipe(map((data) => data));
  }

  getMapsetForConcept(projectId: string, conceptId: string) {
    return this._api.get(`/project/${projectId}/concept/${conceptId}/mappings`).pipe(map((data) => data));
  }

  getNewConcepts(currentVersionId: any, previousVersionId: any) {
    return this._api.get(`/terminology/${currentVersionId}/history/${previousVersionId}/concepts/new`).pipe(map((data) => data));
  }

  exportNewConcepts(termId: string, priorVersionId: string, fileName: string) {
    return this._api.getExport(`/terminology/${termId}/history/${priorVersionId}/concepts/new/export`, fileName);
  }

  getRetiredConcepts(currentVersionId: any, previousVersionId: any) {
    return this._api.get(`/terminology/${currentVersionId}/history/${previousVersionId}/concepts/retired`).pipe(map((data) => data));
  }

  exportRetiredConcepts(termId: string, priorVersionId: string, fileName: string) {
    return this._api.getExport(`/terminology/${termId}/history/${priorVersionId}/concepts/retired/export`, fileName);
  }

  getTerminologyConcepts(
    terminology: string,
    query: string,
    perPage: number,
    page: number,
    sort = 'created',
    ascending = false,
    handler = '',
  ): Observable<any> {
    return this._api
      .get(
        `/terminology/${terminology}/concept?query=${encodeURIComponent(query)}&limit=${perPage}&offset=${page}&sort=${sort}&ascending=${ascending}${handler ? `&handler=${handler}` : ''}`,
      )
      .pipe(map((data) => data));
  }

  exportTerminologyConcepts(terminology: string, query: string, filename: string): Observable<any> {
    return this._api.getExport(`/terminology/${encodeURIComponent(terminology)}/concept/export?query=${encodeURIComponent(query)}`, filename);
  }
}
